import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import { Panel, Loader, useToast, errText, useCaptchaConfirm } from '../ui';

export default function TenantDetail() {
  const { clientCode } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [confirm, confirmNode] = useCaptchaConfirm();

  const [profile, setProfile] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState(null);

  const load = async () => {
    try {
      const res = await tenantApi.getTenant(clientCode);
      setProfile(res.data);
      setForm({
        emailId: res.data.emailId || '',
        mobileNo: res.data.mobileNo || '',
        address: res.data.address || '',
        websiteLink: res.data.websiteLink || '',
        noOfUsers: res.data.noOfUsers || '',
        expiryDate: res.data.expiryDate ? String(res.data.expiryDate).slice(0, 10) : '',
        isPending: !!res.data.isPending,
      });
      if (res.data.clientName) {
        try {
          const db = await tenantApi.getDatabase(res.data.clientName);
          setDbInfo(db);
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      toast.error(errText(err));
    }
  };

  useEffect(() => { load(); }, [clientCode]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, noOfUsers: form.noOfUsers ? Number(form.noOfUsers) : null };
      const res = await tenantApi.updateTenant(clientCode, payload);
      toast.success(res.msg || 'Tenant profile updated.');
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDropDb = async () => {
    const ok = await confirm(
      `Permanently drop database "${profile?.databaseName}"? This deletes all tenant data and cannot be undone.`
    );
    if (!ok) return;
    setBusyAction('drop-db');
    try {
      const res = await tenantApi.deleteDatabase(profile.clientName);
      toast.success(res.msg || 'Database dropped.');
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusyAction(null);
    }
  };

  const handleRemoveRegistration = async () => {
    const ok = await confirm(
      `Remove "${clientCode}" registration from the Master DB? The tenant database itself will NOT be deleted.`
    );
    if (!ok) return;
    setBusyAction('remove-reg');
    try {
      await tenantApi.deleteTenant(clientCode);
      toast.success('Registration removed.');
      navigate('/admin/tenants');
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusyAction(null);
    }
  };

  if (!profile || !form) {
    return (
      <>
        <h1 className="page-title">tenant_detail</h1>
        <Panel title={`// ${clientCode}`}><Loader label="fetching profile" /></Panel>
      </>
    );
  }

  const dbName = profile.databaseName;

  return (
    <>
      {confirmNode}
      <h1 className="page-title">{clientCode}</h1>
      <p className="page-sub">
        <Link to="/admin/tenants" style={{ color: 'var(--text-dim)' }}>← tenant_list</Link>
        {'  /  '}db: <b style={{ color: 'var(--green)' }}>{dbName}</b>
        {dbInfo && <> &nbsp;·&nbsp; {dbInfo.sizeInMB} MB</>}
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        <button className="term-btn term-btn--sm" onClick={() => navigate(`/admin/tenants/${clientCode}/admin-user`)}>admin_credentials</button>
        <button className="term-btn term-btn--sm" onClick={() => navigate(`/admin/tenants/${clientCode}/permissions`)}>permissions</button>
        <button className="term-btn term-btn--sm" onClick={() => navigate(`/admin/tenants/${clientCode}/organization`)}>organization_profile</button>
        <button className="term-btn term-btn--sm" onClick={() => navigate(`/admin/tenants/${clientCode}/seed`)}>forms_and_modules</button>
        <button className="term-btn term-btn--sm" onClick={() => navigate(`/admin/tenants/logs/login-history?clientCode=${clientCode}`)}>login_history</button>
        <button className="term-btn term-btn--sm" onClick={() => navigate(`/admin/tenants/logs/audit?clientCode=${clientCode}`)}>audit_log</button>
        <button className="term-btn term-btn--sm" onClick={() => navigate(`/admin/tenants/logs/exceptions?clientCode=${clientCode}`)}>exception_log</button>
      </div>

      <Panel title="// PROFILE">
        <div className="form-grid">
          <div className="field">
            <label>emailId</label>
            <input className="term-input" value={form.emailId} onChange={(e) => setForm({ ...form, emailId: e.target.value })} />
          </div>
          <div className="field">
            <label>mobileNo</label>
            <input className="term-input" value={form.mobileNo} onChange={(e) => setForm({ ...form, mobileNo: e.target.value })} />
          </div>
          <div className="field field--full">
            <label>address</label>
            <input className="term-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="field">
            <label>websiteLink</label>
            <input className="term-input" value={form.websiteLink} onChange={(e) => setForm({ ...form, websiteLink: e.target.value })} />
          </div>
          <div className="field">
            <label>noOfUsers</label>
            <input type="number" className="term-input" value={form.noOfUsers} onChange={(e) => setForm({ ...form, noOfUsers: e.target.value })} />
          </div>
          <div className="field">
            <label>expiryDate</label>
            <input type="date" className="term-input" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} />
          </div>
          <div className="field">
            <label>isPending</label>
            <select className="term-select" value={form.isPending ? '1' : '0'} onChange={(e) => setForm({ ...form, isPending: e.target.value === '1' })}>
              <option value="0">false — active</option>
              <option value="1">true — pending</option>
            </select>
          </div>
        </div>
        <button className="term-btn term-btn--solid" disabled={saving} onClick={handleSave}>
          {saving ? 'saving...' : 'save_changes'}
        </button>
        <p className="field-hint" style={{ marginTop: 10 }}>
          Only fields you send get updated — unset fields keep their current value server-side.
        </p>
      </Panel>

      <Panel title="// DANGER_ZONE">
        <div className="danger-zone">
          <div className="danger-zone__title">DROP DATABASE</div>
          <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>
            Permanently deletes the tenant&apos;s database ({dbName}). Registration record is untouched.
          </p>
          <button className="term-btn term-btn--danger" disabled={busyAction === 'drop-db'} onClick={handleDropDb}>
            {busyAction === 'drop-db' ? 'dropping...' : 'drop_database'}
          </button>
        </div>
        <div className="danger-zone" style={{ marginTop: 14 }}>
          <div className="danger-zone__title">REMOVE MASTER DB REGISTRATION</div>
          <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>
            Removes this tenant from the registry. Database itself stays intact.
          </p>
          <button className="term-btn term-btn--danger" disabled={busyAction === 'remove-reg'} onClick={handleRemoveRegistration}>
            {busyAction === 'remove-reg' ? 'removing...' : 'remove_registration'}
          </button>
        </div>
      </Panel>
    </>
  );
}
